import { createContext, useContext, useState, useCallback } from 'react';

/**
 * Mock Authentication Context
 *
 * SECURITY NOTE: This app uses MOCKED auth only (no real backend auth).
 * localStorage is used purely for session persistence of mock user data.
 * There are NO real tokens, passwords, or sensitive credentials stored.
 *
 * FOR PRODUCTION:
 * - Use httpOnly cookies for token storage (immune to XSS)
 * - Implement server-side sessions with short-lived JWTs
 * - Add CSRF protection
 * - Use sessionStorage instead of localStorage for less persistent storage
 */
const AuthContext = createContext(null);

const STORAGE_KEY = 'humaclub_user';

const mockUsers = {
  donor: { name: 'Sarah Chen', email: 'sarah@example.com', type: 'donor', avatar: null, initials: 'SC', username: 'sarah-chen', walletAddress: '' },
  ngo: { name: 'Maria Santos', email: 'admin@tampabayoutreach.org', type: 'ngo', avatar: null, initials: 'MS', username: 'maria-santos', orgName: 'Tampa Bay Outreach', walletAddress: '' },
  volunteer: { name: 'Alex Rivera', email: 'alex@volunteer.org', type: 'volunteer', avatar: null, initials: 'AR', username: 'alex-rivera', walletAddress: '' },
  corporate: { name: 'David Park', email: 'david@techcorp.com', type: 'corporate', avatar: null, initials: 'DP', username: 'david-park', company: 'TechCorp', walletAddress: '' },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const isAuthenticated = !!user;

  const login = useCallback((userData) => {
    const u = { ...userData, initials: userData.name?.split(' ').map(w => w[0]).join('').slice(0, 2) || '?' };
    setUser(u);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
  }, []);

  const loginAsType = useCallback((type) => {
    const u = mockUsers[type] || mockUsers.donor;
    login(u);
    return u;
  }, [login]);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, loginAsType, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
